import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box, Paper, Typography, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Divider, Alert, CircularProgress, Dialog,
  DialogTitle, DialogContent, DialogActions, IconButton, Stack,
  useTheme, alpha,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { useVenue } from '../../../../context/VenueContext';
import wineListItemService from '../../../../services/wineListItemService';
import inventoryItemService from '../../../../services/inventoryItemService';
import wineListSectionService from '../../../../services/wineListSectionService';
import wineListFormatService from '../../../../services/wineListFormatService';
import wineListItemFormatService from '../../../../services/wineListItemFormatService';
import WineSearchBox from '../../../../components/venue/WineSearchBox';
import type { WineSearchResult } from '../../../../components/venue/WineSearchBox';
import CreateWineModal from '../../../../components/venue/CreateWineModal';
import type { Wine } from '../../../../services/wineService';
import type { WineListItemVisibility, WineListSection, WineListFormat, WineListItemFormat, WineListScoreSource, WineListScoreEntry } from '../../../../types/venue';
import { WINE_LIST_SCORE_SOURCES, WINE_LIST_SCORE_SOURCE_LABELS } from '../../../../types/venue';
import type { InventoryItemWithBalance } from '../../../../services/inventoryItemService';

interface FormData {
  /** W3: was listPrice. The model field listPrice is still mirror-written by the server. */
  bottlePrice: string;
  currency: string;
  binNumber: string;
  visibility: WineListItemVisibility;
  notes: string;
  bottleSize: string;
  /** '' = unsectioned. Otherwise the section _id. */
  sectionId: string;
  /** W7C — optional external URL (tasting note, producer page, …). '' = no link. */
  externalUrl: string;
}

/** W8 — multi-score editor row. Source/score may be empty during typing. */
interface ScoreRow {
  source: WineListScoreSource | '';
  /** Stored as text so partial typing works; validated on save. */
  score: string;
}

/**
 * One row in the per-item format pricing UI. May represent either an existing
 * server-side WineListItemFormat (then `_id` is set) or a draft to be created
 * on save (only formatId + price filled in).
 */
interface FormatRow {
  /** Server id when persisted, undefined when this row is new. */
  _id?: string;
  formatId: string;
  /** Free-text price to support typing in progress. Validated on save. */
  price: string;
}

const WineListItemForm: React.FC = () => {
  const navigate = useNavigate();
  const { wineListId, itemId } = useParams<{ wineListId: string; itemId: string }>();
  const theme = useTheme();
  const { venueId, defaultCurrency } = useVenue();
  const isEdit = Boolean(itemId);
  const backPath = `/admin/venues/${venueId}/wine-lists/${wineListId}`;

  const [selectedWine, setSelectedWine] = useState<WineSearchResult | null>(null);
  const [wineError, setWineError] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalQuery, setCreateModalQuery] = useState('');

  // For edit mode: display the wine name read-only
  const [editWineDisplay, setEditWineDisplay] = useState('');

  // Inventory item link
  const [linkedItem, setLinkedItem] = useState<InventoryItemWithBalance | null>(null);
  const [itemSearchResults, setItemSearchResults] = useState<InventoryItemWithBalance[]>([]);
  const [itemSearchLoading, setItemSearchLoading] = useState(false);

  // Confirmation dialog when selected item's bottleSize differs from existing
  const [confirmPending, setConfirmPending] = useState<InventoryItemWithBalance | null>(null);

  // Tracks that the loaded item has an inventoryItemId — locks bottleSize before async fetch completes
  const [editHasLinkedItemId, setEditHasLinkedItemId] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    bottlePrice: '',
    currency: defaultCurrency,
    binNumber: '',
    visibility: 'LIVE',
    notes: '',
    bottleSize: '',
    sectionId: '',
    externalUrl: '',
  });

  // W8 — multi-score editor. Collapsed by default; the summary line shows
  // the current scores, and an Edit toggle reveals the row editor.
  const [scoreRows, setScoreRows] = useState<ScoreRow[]>([]);
  const [scoresExpanded, setScoresExpanded] = useState(false);

  // Sections for the section selector
  const [sections, setSections] = useState<WineListSection[]>([]);

  // Formats catalog for this list (W3) — drives the format pricing dropdown.
  const [formats, setFormats] = useState<WineListFormat[]>([]);

  // Per-item format pricing rows. When loading an existing item, populated
  // from the server. Saved diff-style on submit (create new, update changed,
  // delete removed).
  const [formatRows, setFormatRows] = useState<FormatRow[]>([]);
  const [originalFormatRows, setOriginalFormatRows] = useState<FormatRow[]>([]);

  useEffect(() => {
    if (!wineListId) return;
    wineListSectionService.getSections(venueId, wineListId)
      .then(setSections)
      .catch(() => { /* non-blocking */ });
    wineListFormatService.getFormats(venueId, wineListId)
      .then(setFormats)
      .catch(() => { /* non-blocking */ });
  }, [venueId, wineListId]);

  // Load existing per-item format pricings in edit mode (after the item itself loads).
  useEffect(() => {
    if (!isEdit || !itemId) return;
    wineListItemFormatService.getItemFormats(venueId, itemId)
      .then((rows: WineListItemFormat[]) => {
        const mapped: FormatRow[] = rows.map(r => ({
          _id: r._id,
          formatId: typeof r.formatId === 'string' ? r.formatId : r.formatId._id,
          price: String(r.price),
        }));
        setFormatRows(mapped);
        setOriginalFormatRows(mapped);
      })
      .catch(() => { /* non-blocking */ });
  }, [isEdit, itemId, venueId]);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [loading, setLoading] = useState(isEdit);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');

  // ── Load existing item in edit mode ──────────────────────────────────────────
  useEffect(() => {
    if (!isEdit || !itemId || !wineListId) return;
    setLoading(true);
    wineListItemService.getItem(venueId, wineListId, itemId)
      .then(item => {
        setEditWineDisplay(item.wineDisplay?.name ?? item.wineId);
        // W3: prefer bottlePrice, fall back to legacy listPrice for older items.
        const effectivePrice = item.bottlePrice ?? item.listPrice;
        setFormData({
          bottlePrice: effectivePrice != null ? String(effectivePrice) : '',
          currency: item.currency ?? defaultCurrency,
          binNumber: item.binNumber ?? '',
          visibility: item.visibility,
          notes: item.notes ?? '',
          bottleSize: item.bottleSize != null ? String(item.bottleSize) : '',
          sectionId: item.sectionId ?? '',
          externalUrl: item.externalUrl ?? '',
        });
        const existingScores = (item.scores ?? []).map(s => ({
          source: s.source,
          score: String(s.score),
        }));
        setScoreRows(existingScores);
        // Pre-expand when there are scores so the user immediately sees them.
        setScoresExpanded(existingScores.length > 0);
        if (item.inventoryItemId) {
          setEditHasLinkedItemId(true);
          inventoryItemService.getItem(venueId, item.inventoryItemId)
            .then(inv => {
              const invItem = inv as unknown as InventoryItemWithBalance;
              setLinkedItem(invItem);
              // Sync displayed bottleSize with inventory item — corrects any DB drift
              if (invItem.sizeValue != null) {
                setFormData(prev => ({ ...prev, bottleSize: String(invItem.sizeValue) }));
              }
            })
            .catch(() => { /* non-blocking */ });
        }
      })
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
  }, [isEdit, itemId, wineListId, venueId, defaultCurrency]);

  // ── Debounced inventory item search ──────────────────────────────────────────
  const runItemSearch = useMemo(
    () => debounce(async (q: string) => {
      if (!q.trim()) { setItemSearchResults([]); setItemSearchLoading(false); return; }
      setItemSearchLoading(true);
      try {
        const res = await inventoryItemService.getItems(venueId, 1, 20, 'displayName', 'asc', q);
        setItemSearchResults(res.data ?? []);
      } catch {
        setItemSearchResults([]);
      } finally {
        setItemSearchLoading(false);
      }
    }, 350),
    [venueId],
  );
  useEffect(() => () => runItemSearch.cancel(), [runItemSearch]);

  // ── Inventory item selection ───────────────────────────────────────────────────
  const applyInventoryItem = (item: InventoryItemWithBalance) => {
    setLinkedItem(item);
    setFormData(prev => ({ ...prev, bottleSize: item.sizeValue != null ? String(item.sizeValue) : '' }));
  };

  const handleInventoryItemSelect = (item: InventoryItemWithBalance | null) => {
    if (!item) {
      setLinkedItem(null);
      return;
    }
    const currentSize = formData.bottleSize ? parseFloat(formData.bottleSize) : null;
    const newSize = item.sizeValue ?? null;
    // Warn only when sizes actively differ (both non-null and not equal)
    if (currentSize != null && newSize != null && currentSize !== newSize) {
      setConfirmPending(item);
    } else {
      applyInventoryItem(item);
    }
  };

  // ── Generic field change ──────────────────────────────────────────────────────
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!isEdit && !selectedWine) {
      setWineError('Please select a wine.');
      return false;
    }
    if (formData.bottlePrice && isNaN(parseFloat(formData.bottlePrice))) {
      newErrors.bottlePrice = 'Must be a valid number.';
    }
    if (formData.bottleSize && isNaN(parseFloat(formData.bottleSize))) {
      newErrors.bottleSize = 'Must be a valid number.';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return false;

    // W8 — scores validation: each row must have a source AND a numeric
    // 50–100 score; no duplicate sources allowed. We surface the first
    // problem via apiError to keep the multi-row UX simple.
    const seenSources = new Set<string>();
    for (const row of scoreRows) {
      if (!row.source) { setApiError('Every score row needs a source.'); return false; }
      if (seenSources.has(row.source)) {
        setApiError(`Score source "${row.source}" appears more than once — keep at most one per source.`);
        return false;
      }
      seenSources.add(row.source);
      const trimmed = row.score.trim();
      if (!trimmed) { setApiError(`Add a score for ${row.source} or remove the row.`); return false; }
      const n = parseFloat(trimmed);
      if (isNaN(n) || n < 50 || n > 100) {
        setApiError(`${row.source} score must be a number between 50 and 100.`);
        return false;
      }
    }

    // Format pricing rows must each have a format selected and a numeric price >= 0.
    for (const row of formatRows) {
      if (!row.formatId) { setApiError('Every format pricing row needs a format selected.'); return false; }
      if (!row.price || isNaN(parseFloat(row.price)) || parseFloat(row.price) < 0) {
        setApiError('Every format pricing row needs a non-negative price.');
        return false;
      }
    }
    // No duplicate formats across rows.
    const seen = new Set<string>();
    for (const row of formatRows) {
      if (seen.has(row.formatId)) {
        setApiError('A format can be priced only once per wine.');
        return false;
      }
      seen.add(row.formatId);
    }
    return true;
  };

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (!wineListId) return;
    setSaving(true);
    setApiError('');
    try {
      const bottlePrice = formData.bottlePrice ? parseFloat(formData.bottlePrice) : undefined;
      const bottleSize = formData.bottleSize ? parseFloat(formData.bottleSize) : undefined;
      // W8 — score pair: send both or neither. Empty pair becomes null/null
      // so the server clears any previously stored values.
      // W8 — convert the editable score rows into the wire format. validateForm
      // already guarantees source/score are present and numeric.
      const cleanedScores: WineListScoreEntry[] = scoreRows.map(r => ({
        source: r.source as WineListScoreSource,
        score: parseFloat(r.score),
      }));

      // Save the item itself first; we need an item id before we can write per-item formats.
      let savedItemId = itemId;
      if (isEdit && itemId) {
        await wineListItemService.updateItem(venueId, wineListId, itemId, {
          bottlePrice,
          currency: formData.currency.trim() || undefined,
          binNumber: formData.binNumber.trim() || undefined,
          visibility: formData.visibility,
          notes: formData.notes.trim() || undefined,
          bottleSize: bottleSize ?? undefined,
          // sectionId: '' clears the assignment (server treats '' as null)
          sectionId: formData.sectionId === '' ? null : formData.sectionId,
          // W7C — explicit empty string clears the link server-side; non-empty saves it.
          externalUrl: formData.externalUrl.trim(),
          // W8 — always send the array; empty array clears existing scores.
          scores: cleanedScores,
          // null → set: include inventoryItemId only when linking during this edit session
          ...(!editHasLinkedItemId && linkedItem ? { inventoryItemId: linkedItem._id } : {}),
        });
      } else {
        const created = await wineListItemService.createItem(venueId, wineListId, {
          wineId: selectedWine!._id,
          ...(linkedItem && { inventoryItemId: linkedItem._id }),
          bottlePrice,
          currency: formData.currency.trim() || undefined,
          binNumber: formData.binNumber.trim() || undefined,
          visibility: formData.visibility,
          source: 'MANUAL',
          notes: formData.notes.trim() || undefined,
          active: true,
          ...(bottleSize != null && { bottleSize }),
          ...(formData.sectionId && { sectionId: formData.sectionId }),
          // W7C — only sent when the admin actually entered a URL.
          ...(formData.externalUrl.trim() && { externalUrl: formData.externalUrl.trim() }),
          // W8 — only sent when there's at least one score row.
          ...(cleanedScores.length > 0 && { scores: cleanedScores }),
        } as Parameters<typeof wineListItemService.createItem>[2]);
        savedItemId = created._id;
      }

      // ── Diff and persist per-item format pricings (W3) ────────────────────
      // For an existing item: delete rows removed in the form, update rows whose
      // price changed, create new rows. For a brand-new item: just create them all.
      if (savedItemId) {
        const originalById = new Map(originalFormatRows.filter(r => r._id).map(r => [r._id!, r]));
        const currentIds = new Set(formatRows.filter(r => r._id).map(r => r._id!));

        // Deletes: original ids no longer present in the current rows.
        for (const orig of originalFormatRows) {
          if (orig._id && !currentIds.has(orig._id)) {
            await wineListItemFormatService.deleteItemFormat(venueId, savedItemId, orig._id);
          }
        }
        // Creates + updates.
        for (const row of formatRows) {
          const priceNum = parseFloat(row.price);
          if (!row._id) {
            await wineListItemFormatService.createItemFormat(venueId, savedItemId, {
              formatId: row.formatId,
              price: priceNum,
            });
          } else {
            const orig = originalById.get(row._id);
            if (!orig || parseFloat(orig.price) !== priceNum) {
              await wineListItemFormatService.updateItemFormat(venueId, savedItemId, row._id, {
                price: priceNum,
              });
            }
          }
        }
      }
      navigate(backPath);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      const backendMessage = err?.response?.data?.message;
      if (code === 'WINE_ALREADY_ON_LIST') {
        // W10C — the backend now distinguishes linked vs unlinked + size
        // collisions and sends the right copy for each. Trust it; only fall
        // back to a generic line if for some reason the body is empty.
        setApiError(backendMessage ?? 'This wine and bottle size are already on this list.');
      } else {
        setApiError(backendMessage ?? 'Failed to save. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Label for inventory item in autocomplete ───────────────────────────────────
  const inventoryItemLabel = (item: InventoryItemWithBalance) => {
    const name = item.displayName;
    const size = item.sizeValue != null ? `${item.sizeValue} ${item.sizeUnit ?? 'ml'}` : null;
    return size ? `${name} · ${size}` : name;
  };

  const bottleSizeLocked = Boolean(linkedItem) || editHasLinkedItemId;

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadFailed) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary, alignSelf: 'flex-start' }}>
          Wine List
        </Button>
        <Alert severity="error">
          Could not load this entry. It may have been removed. Go back and try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
            Wine List
          </Button>
          <Divider orientation="vertical" flexItem />
          <Typography variant="h6" fontWeight={600}>{isEdit ? 'Edit Entry' : 'Add Wine to List'}</Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {apiError && <Alert severity="error" sx={{ borderRadius: 1 }}>{apiError}</Alert>}

          {/* Wine selection */}
          {isEdit ? (
            <TextField
              label="Wine"
              value={editWineDisplay}
              fullWidth
              disabled
              helperText="Wine cannot be changed after adding. Remove and re-add to change the wine."
            />
          ) : (
            <>
              <WineSearchBox
                value={selectedWine}
                onChange={wine => { setSelectedWine(wine); setWineError(''); }}
                onCreateNew={query => { setCreateModalQuery(query); setCreateModalOpen(true); }}
                error={Boolean(wineError)}
                helperText={wineError || undefined}
                label="Wine"
              />
              {selectedWine && (
                <Box sx={{ display: 'flex', gap: 2, px: 0.25 }}>
                  {selectedWine.wineDisplay?.producerName && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.68rem' }}>Producer</Typography>
                      <Typography variant="body2">{selectedWine.wineDisplay.producerName}</Typography>
                    </Box>
                  )}
                  {selectedWine.wineDisplay?.regionName && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.68rem' }}>Region</Typography>
                      <Typography variant="body2">{selectedWine.wineDisplay.regionName}</Typography>
                    </Box>
                  )}
                  {selectedWine.displayVintage && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.68rem' }}>Vintage</Typography>
                      <Typography variant="body2">{selectedWine.displayVintage}</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}

          {/* Inventory item link */}
          <Autocomplete
            size="small"
            options={itemSearchResults}
            value={linkedItem}
            loading={itemSearchLoading}
            filterOptions={x => x}
            getOptionLabel={inventoryItemLabel}
            isOptionEqualToValue={(opt, val) => opt._id === val._id}
            disabled={isEdit && Boolean(linkedItem || editHasLinkedItemId)}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') runItemSearch(value);
              if (reason === 'clear') { setLinkedItem(null); setItemSearchResults([]); }
            }}
            onChange={(_, val) => handleInventoryItemSelect(val)}
            renderOption={(props, opt) => (
              <Box component="li" {...props} key={opt._id}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>{inventoryItemLabel(opt)}</Typography>
                  {opt.balance != null && (
                    <Typography variant="caption" color="text.secondary">{opt.balance.totalQuantity} in stock</Typography>
                  )}
                </Box>
              </Box>
            )}
            renderInput={params => (
              <TextField
                {...params}
                label="Linked Inventory Item (optional)"
                placeholder="Search to link a specific inventory item…"
                helperText={isEdit && Boolean(linkedItem || editHasLinkedItemId)
                  ? 'Inventory item link cannot be changed after creation.'
                  : 'Links this entry to a tracked inventory item. Allows the same wine in different formats on the same list.'}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {itemSearchLoading && <CircularProgress size={14} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />

          {/* Bottle Price + Currency + Bottle Size + Bin */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Bottle Price"
              name="bottlePrice"
              value={formData.bottlePrice}
              onChange={handleInputChange}
              error={Boolean(errors.bottlePrice)}
              helperText={errors.bottlePrice}
              type="number"
              sx={{ flex: 2, minWidth: 120 }}
              slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
            />
            <TextField
              label="Currency"
              name="currency"
              value={formData.currency}
              onChange={handleInputChange}
              sx={{ width: 110 }}
              slotProps={{ htmlInput: { maxLength: 3, style: { textTransform: 'uppercase' } } }}
            />
            <TextField
              label="Bottle Size (ml)"
              name="bottleSize"
              value={formData.bottleSize}
              onChange={handleInputChange}
              error={Boolean(errors.bottleSize)}
              helperText={bottleSizeLocked ? 'Set by linked inventory item' : errors.bottleSize}
              type="number"
              sx={{ width: 150 }}
              placeholder="e.g. 750"
              disabled={bottleSizeLocked}
              slotProps={{ htmlInput: { min: 1, step: 1 } }}
            />
            <TextField
              label="Bin Number"
              name="binNumber"
              value={formData.binNumber}
              onChange={handleInputChange}
              sx={{ flex: 1, minWidth: 100 }}
              placeholder="e.g. C-14"
            />
          </Box>

          {/* Visibility + Section */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 240, flex: 1, maxWidth: 320 }}>
              <InputLabel>Visibility</InputLabel>
              <Select
                value={formData.visibility}
                label="Visibility"
                onChange={e => setFormData(prev => ({ ...prev, visibility: e.target.value as WineListItemVisibility }))}
              >
                <MenuItem value="LIVE">Live — visible on list</MenuItem>
                <MenuItem value="HIDDEN">Hidden — not shown</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 240, flex: 1, maxWidth: 320 }}>
              <InputLabel>Section</InputLabel>
              <Select
                value={formData.sectionId}
                label="Section"
                onChange={e => setFormData(prev => ({ ...prev, sectionId: e.target.value }))}
              >
                <MenuItem value="">
                  <em>Unsectioned</em>
                </MenuItem>
                {sections.map(s => (
                  <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Notes */}
          <TextField
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            fullWidth
            multiline
            rows={3}
            placeholder="Tasting notes or list copy (optional)"
          />

          {/* W7C — optional external link. Stored as-is, no URL validation;
              shown to customers as a "View details" link in the detail modal. */}
          <TextField
            label="External link"
            name="externalUrl"
            value={formData.externalUrl}
            onChange={handleInputChange}
            fullWidth
            placeholder="https://..."
            helperText="Optional. Tasting note, producer page, importer sheet, etc. Shown as 'View details' on the customer view."
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />

          {/* W8 — multi-score editor. Collapsed by default. Header line shows
              the current scores (e.g. "Scores: JS96, WA95"); clicking expands
              the row editor. Each row = source + numeric score; max one row
              per source; remove buttons clear individual scores. */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: scoresExpanded ? 1 : 0 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>Scores</Typography>
                <Typography variant="caption" color="text.secondary">
                  {scoreRows.length === 0
                    ? 'Optional. Add one or more critic scores; shown as small badges on the customer view.'
                    : scoreRows.filter(r => r.source && r.score).map(r => `${r.source}${r.score}`).join(', ') || 'No scores yet'}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => setScoresExpanded(v => !v)}
                sx={{ textTransform: 'none', fontWeight: 600, p: 0, minWidth: 0, color: 'primary.main', '&:hover': { background: 'none', textDecoration: 'underline' } }}
              >
                {scoresExpanded ? 'Hide' : 'Edit'}
              </Button>
            </Box>

            {scoresExpanded && (
              <Box sx={{ mt: 1 }}>
                <Stack spacing={1.25}>
                  {scoreRows.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No scores yet. Click "Add score" to add one.
                    </Typography>
                  )}
                  {scoreRows.map((row, idx) => {
                    const usedElsewhere = new Set(
                      scoreRows.filter((_, i) => i !== idx).map(r => r.source).filter(Boolean),
                    );
                    return (
                      <Box key={idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Source</InputLabel>
                          <Select
                            value={row.source}
                            label="Source"
                            onChange={e => {
                              const value = e.target.value as WineListScoreSource | '';
                              setScoreRows(rows => rows.map((r, i) => i === idx ? { ...r, source: value } : r));
                            }}
                          >
                            {WINE_LIST_SCORE_SOURCES.map(src => (
                              <MenuItem
                                key={src}
                                value={src}
                                disabled={usedElsewhere.has(src) && row.source !== src}
                              >
                                {src} — {WINE_LIST_SCORE_SOURCE_LABELS[src]}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          size="small"
                          label="Score"
                          value={row.score}
                          onChange={e => {
                            const value = e.target.value;
                            setScoreRows(rows => rows.map((r, i) => i === idx ? { ...r, score: value } : r));
                          }}
                          type="number"
                          sx={{ width: 110 }}
                          slotProps={{ htmlInput: { min: 50, max: 100, step: 1 } }}
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setScoreRows(rows => rows.filter((_, i) => i !== idx))}
                          aria-label="Remove score"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Stack>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  disabled={scoreRows.length >= WINE_LIST_SCORE_SOURCES.length}
                  onClick={() => setScoreRows(rows => [...rows, { source: '', score: '' }])}
                  sx={{ textTransform: 'none', mt: 1 }}
                >
                  Add score
                </Button>
              </Box>
            )}
          </Box>

          {/* Per-item format pricing (W3) — bottle is implicit; this is for glass / coravin / etc. */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>Format pricing</Typography>
                <Typography variant="caption" color="text.secondary">
                  Set a price per pour format. Bottle price is set above. Manage the format catalog from the Formats tab.
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<AddIcon />}
                disabled={formats.length === 0 || formatRows.length >= formats.length}
                onClick={() => setFormatRows(rows => [...rows, { formatId: '', price: '' }])}
                sx={{ textTransform: 'none' }}
              >
                Add format
              </Button>
            </Box>
            {formats.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No formats defined on this list yet — add some in the Formats tab to enable per-pour pricing.
              </Typography>
            ) : formatRows.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No format prices on this wine. Click "Add format" to add one.
              </Typography>
            ) : (
              <Stack spacing={1.25}>
                {formatRows.map((row, idx) => {
                  // Disable formats already chosen on another row to prevent duplicates.
                  const usedElsewhere = new Set(
                    formatRows.filter((_, i) => i !== idx).map(r => r.formatId).filter(Boolean),
                  );
                  return (
                    <Box key={idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      <FormControl size="small" sx={{ flex: 2, minWidth: 180 }}>
                        <InputLabel>Format</InputLabel>
                        <Select
                          value={row.formatId}
                          label="Format"
                          onChange={e => {
                            const value = e.target.value as string;
                            setFormatRows(rows => rows.map((r, i) => i === idx ? { ...r, formatId: value } : r));
                          }}
                        >
                          {formats.map(f => (
                            <MenuItem
                              key={f._id}
                              value={f._id}
                              disabled={usedElsewhere.has(f._id) && row.formatId !== f._id}
                            >
                              {f.name} · {f.volumeMl} ml
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        label="Price"
                        value={row.price}
                        onChange={e => {
                          const value = e.target.value;
                          setFormatRows(rows => rows.map((r, i) => i === idx ? { ...r, price: value } : r));
                        }}
                        type="number"
                        sx={{ width: 130 }}
                        slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setFormatRows(rows => rows.filter((_, i) => i !== idx))}
                        aria-label="Remove format pricing"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>

          {/* Actions */}
          <Box sx={{ pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 600, minWidth: 140 }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : isEdit ? 'Save Changes' : 'Add to List'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Bottle size mismatch confirmation */}
      <Dialog open={Boolean(confirmPending)} onClose={() => setConfirmPending(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Update bottle size?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The selected inventory item has a different bottle size
            {confirmPending?.sizeValue != null && ` (${confirmPending.sizeValue} ${confirmPending.sizeUnit ?? 'ml'})`}.
            This will replace the current bottle size on this wine list entry.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmPending(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (confirmPending) applyInventoryItem(confirmPending);
              setConfirmPending(null);
            }}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <CreateWineModal
        open={createModalOpen}
        initialQuery={createModalQuery}
        onCreated={(wine: Wine) => {
          const asSearchResult: WineSearchResult = {
            _id: wine._id,
            name: wine.name,
            displayVintage: wine.displayVintage,
            vintageType: wine.vintageType,
            wineDisplay: {
              producerName: wine.wineName?.producer?.name,
              regionName: wine.wineName?.region?.name,
              color: wine.wineName?.color,
            },
          };
          setSelectedWine(asSearchResult);
          setWineError('');
          setCreateModalOpen(false);
        }}
        onClose={() => setCreateModalOpen(false)}
      />
    </Box>
  );
};

export default WineListItemForm;
